import { useEffect, useMemo, useState, type FC } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { FormProvider, useForm } from 'react-hook-form';
import Button, { type ButtonSkin } from '2-core-elements/Buttons/Button';
import preventEnterKeySubmission from '5-react-form-apps/Utils/preventEnterKeySubmission';
import TopUpAmount from './Forms/TopUpAmount/TopUpAmount';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import InlineMessage from '3-components/InlineMessage/InlineMessage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useAccountBalance from './Utils/useAccountBalance';
import useTopUpMutation from './Utils/useTopUpMutation';
import MethodOptions from './Forms/MethodOptions/MethodOptions';
import { pushGTMEvent } from 'utils/eventGTM';

export type TopUpFormData = {
    topUpType: string;
    amount?: number;
    otherAmount?: number;
}

interface TopUpProps {
    accountLimit?: number;
    topUpViaCreditCardTitle?: string;
    topUpViaCreditCardDescription?: string;
    topUpViaBankTransferTitle?: string;
    topUpViaBankTransferDescription?: string;
    pageId?: string;
    bankTransferDetailsRows?: {
        rowName: string;
        rowValue: string;
    }[];
}

const TopUp: FC<TopUpProps> = ({ accountLimit, topUpViaBankTransferDescription, topUpViaBankTransferTitle, topUpViaCreditCardDescription, topUpViaCreditCardTitle, pageId, bankTransferDetailsRows }) => {
    const methods = useForm<TopUpFormData>({ defaultValues: { topUpType: 'credit-card' }, mode: 'onChange' });
    const [topUpLimit, setTopUpLimit] = useState(0);
    const { handleSubmit, watch, formState: { errors } } = methods;
    const [topUpType, amount, otherAmount] = watch(['topUpType', 'amount', 'otherAmount']);
    const accountBalance = useAccountBalance();
    const { mutate: submitFormMutate, isLoading } = useTopUpMutation();

    const buttonState = useMemo((): {title: string; disabled: boolean; skin: ButtonSkin} => {
        if (topUpType === 'bank-transfer') {
            return { title: 'Back to MCC Plus', disabled: false, skin: '2' };
        }
        return {
            title: 'Top up card',
            disabled: (!amount && (!otherAmount)) || errors.otherAmount !== undefined,
            skin: '1',
        };
    }, [topUpType, amount, otherAmount, errors.otherAmount]);

    const onSubmit = handleSubmit(value => {
        if (topUpType === 'credit-card' && !buttonState.disabled) {
            const amountToSubmit = value.amount || value.otherAmount || 0;

            if (amountToSubmit <= 0) {
                throw new Error('Amount must be greater than zero');
            }

            if (!pageId) {
                return;
            }

            submitFormMutate({ amount: amountToSubmit, pageId }, {
                onSuccess: data => {
                    if (data.isSuccess) {
                        const redirectUrl = data?.data?.redirectUrl;

                        if (!redirectUrl) {
                            throw new Error('Redirect URL is not available');
                        }

                        pushGTMEvent({
                            actionType: 'form_complete',
                            name: 'Top up',
                            type: 'Top up',
                        });

                        window.location.href = redirectUrl;
                    } else {
                        throw new Error(data.errorMessages?.[0] || 'Unknown error occurred');
                    }
                },
            });
        } else if (topUpType === 'bank-transfer') {
            if (window.history.length > 1) {
                window.history.back();
            } else {
                window.location.replace('/styleguide/my-MCC/mcc-plus.html');
            }
        }
    });

    useEffect(() => {
        if (accountBalance.isSuccess && accountBalance.data && accountLimit && accountLimit > 0) {
            const limit = (accountLimit || 0) - accountBalance.data.currentBalance;
            const limitFormatted = Number.parseFloat(limit.toFixed(2));
            setTopUpLimit(limitFormatted);
        }
    }, [accountBalance.data, accountBalance.isSuccess, accountLimit]);

    useEffect(() => {
        if (accountBalance.isSuccess && pageId) {
            pushGTMEvent({
                actionType: 'form_start',
                name: 'Top up',
                type: 'Top up',
            });
        }
    }, [accountBalance.isSuccess, pageId]);

    useEffect(() => {
        // Top up only has this one error field
        if (errors.otherAmount) {
            pushGTMEvent({
                actionType: 'form_error',
                name: 'Top up',
                type: 'Top up',
                step_name: 'Top up',
                step_number: 1,
                total_steps: 1,
                message: errors.otherAmount.message || '',
            });
        }
    }, [errors.otherAmount]);

    if (accountBalance.isLoading) {
        return (
            <div>
                <LoadingIndicator className="sg-eg-loader" />
            </div>
        );
    }

    if (!accountBalance.isSuccess) {
        return (
            <div className="container">
                <InlineMessage status="error">
                    <p>{accountBalance.error instanceof Error ? accountBalance.error.message : 'Error loading user'}</p>
                </InlineMessage>
            </div>
        );
    }

    if (!pageId) {
        return (
            <div className="container">
                <InlineMessage status="error">
                    <p>Could not load the page</p>
                </InlineMessage>
            </div>
        );
    }

    return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <FormProvider {...methods}>
            <form className="form" onSubmit={onSubmit} onKeyDown={preventEnterKeySubmission}>
                <div className="top-up-wrapper">
                    <h1 className="top-up-title">Top Up</h1>

                    <div className="top-up-balance">
                        <div className="top-up-balance-item">
                            <span className="top-up-balance-label">Current balance</span>
                            <span className="top-up-balance-value">${accountBalance.data.currentBalance}</span>
                        </div>
                        <div className="top-up-balance-item">
                            <span className="top-up-balance-label">Account limit</span>
                            <span className="top-up-balance-value top-up-balance-value--strong">${accountLimit}</span>
                        </div>
                    </div>

                    <MethodOptions
                        topUpViaCreditCardTitle={topUpViaCreditCardTitle}
                        topUpViaCreditCardDescription={topUpViaCreditCardDescription}
                        topUpViaBankTransferTitle={topUpViaBankTransferTitle}
                        topUpViaBankTransferDescription={topUpViaBankTransferDescription}
                        bankTransferDetailsRows={bankTransferDetailsRows}
                    />

                    {topUpLimit > 0 && topUpType === 'credit-card' && <TopUpAmount topUpLimit={topUpLimit} />}

                    {isLoading ? (
                        <div>
                            <LoadingIndicator className="sg-eg-loader" />
                        </div>
                    ) : (
                        <Button
                            skin={buttonState.skin}
                            disabled={buttonState.disabled}
                            onClick={onSubmit}
                            type="submit"
                        >
                            {buttonState.title}
                        </Button>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};

const queryClient = new QueryClient();

interface TopUpInitProps {
    el: HTMLDivElement;
    props: TopUpProps;
}

const TopUpInit = ({ el, props }: TopUpInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <TopUp
                accountLimit={props.accountLimit}
                topUpViaBankTransferDescription={props.topUpViaBankTransferDescription}
                topUpViaBankTransferTitle={props.topUpViaBankTransferTitle}
                topUpViaCreditCardDescription={props.topUpViaCreditCardDescription}
                topUpViaCreditCardTitle={props.topUpViaCreditCardTitle}
                pageId={props.pageId}
                bankTransferDetailsRows={props.bankTransferDetailsRows}
            />
        </QueryClientProvider>
    );
};

export default TopUpInit;
